import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service layer for subscription and feature access management
 * Handles plan validation, feature gates, and usage tracking
 */

export const subscriptionService = {
  /**
   * Get current user's subscription with plan details
   * @returns {Promise<{data, error}>}
   */
  async getCurrentSubscription() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      // Fetch subscription with plan details
      // Use maybeSingle() instead of single() to handle users without subscriptions
      const { data, error } = await supabase
        ?.from('user_subscriptions')
        ?.select(`
          *,
          plan:subscription_plans(*)
        `)
        ?.eq('user_id', user?.id)
        ?.maybeSingle();
      
      if (error) {
        logger?.error('Failed to fetch subscription', error, { requestId, userId: user?.id });
        return { data: null, error };
      }
      
      // If no subscription found, return null (user doesn't have a subscription)
      if (!data) {
        logger?.debug('No subscription found for user', { requestId, userId: user?.id });
        return { data: null, error: null };
      }
      
      // Log raw data for debugging
      console.log('Raw subscription data from DB:', {
        plan_id: data?.plan_id,
        plan_type: data?.plan?.plan_type,
        plan_name: data?.plan?.name,
        status: data?.status
      });
      
      // Convert snake_case to camelCase
      const subscription = {
        id: data?.id,
        userId: data?.user_id,
        planId: data?.plan_id,
        status: data?.status,
        billingInterval: data?.billing_interval,
        currentPeriodStart: data?.current_period_start,
        currentPeriodEnd: data?.current_period_end,
        cancelAtPeriodEnd: data?.cancel_at_period_end,
        cancelledAt: data?.cancelled_at,
        trialStart: data?.trial_start,
        trialEnd: data?.trial_end,
        stripeCustomerId: data?.stripe_customer_id,
        stripeSubscriptionId: data?.stripe_subscription_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        plan: {
          id: data?.plan?.id,
          planType: data?.plan?.plan_type,
          name: data?.plan?.name,
          description: data?.plan?.description,
          priceMonthly: data?.plan?.price_monthly,
          priceYearly: data?.plan?.price_yearly,
          features: data?.plan?.features,
          limits: data?.plan?.limits,
          isActive: data?.plan?.is_active
        }
      };
      
      logger?.debug('Subscription fetched', { requestId, planType: subscription?.plan?.planType });
      return { data: subscription, error: null };
    } catch (error) {
      logger?.error('Subscription fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch subscription' } };
    }
  },

  /**
   * Get all available subscription plans
   * @returns {Promise<{data, error}>}
   */
  async getAvailablePlans() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data, error } = await supabase
        ?.from('subscription_plans')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('price_monthly', { ascending: true });
      
      if (error) {
        logger?.error('Failed to fetch plans', error, { requestId });
        return { data: null, error };
      }
      
      // Convert snake_case to camelCase
      const plans = data?.map(plan => ({
        id: plan?.id,
        planType: plan?.plan_type,
        name: plan?.name,
        description: plan?.description,
        priceMonthly: plan?.price_monthly,
        priceYearly: plan?.price_yearly,
        features: plan?.features,
        limits: plan?.limits,
        isActive: plan?.is_active
      }));
      
      logger?.debug('Plans fetched', { requestId, count: plans?.length });
      return { data: plans, error: null };
    } catch (error) {
      logger?.error('Plans fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch plans' } };
    }
  },

  /**
   * Check if user has access to a specific feature
   * @param {string} featureName - Feature to check
   * @returns {Promise<{hasAccess: boolean, reason: string, limit: number, used: number}>}
   */
  async checkFeatureAccess(featureName) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      // Check if user is owner - grant full access
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user?.email?.toLowerCase() === 'kingsley@mybodytoday1.com') {
        return { hasAccess: true, reason: 'Owner access - unrestricted', limit: -1, used: 0 };
      }
      
      const { data: subscription } = await this.getCurrentSubscription();
      if (!subscription) {
        return { hasAccess: false, reason: 'No active subscription', limit: 0, used: 0 };
      }
      
      const features = subscription?.plan?.features || {};
      const limits = subscription?.plan?.limits || {};
      
      // Check if feature is enabled in plan
      if (!features?.[featureName]) {
        return { 
          hasAccess: false, 
          reason: `Feature not available in ${subscription?.plan?.name}`,
          limit: 0,
          used: 0
        };
      }
      
      // Check usage limits for features with limits
      // Support both formats: featureName_limit and featureName
      const limitKey = `${featureName}_limit`;
      const limitValue = limits?.[limitKey] !== undefined ? limits?.[limitKey] : limits?.[featureName];
      
      if (limitValue !== undefined && limitValue !== -1) {
        const usage = await this.getFeatureUsage(featureName);
        if (usage?.usageCount >= limitValue) {
          return {
            hasAccess: false,
            reason: `Monthly limit reached (${limitValue})`,
            limit: limitValue,
            used: usage?.usageCount
          };
        }
        
        return {
          hasAccess: true,
          reason: 'Access granted',
          limit: limitValue,
          used: usage?.usageCount
        };
      }
      
      return { hasAccess: true, reason: 'Access granted', limit: -1, used: 0 };
    } catch (error) {
      logger?.error('Feature access check error', error, { requestId, featureName });
      return { hasAccess: false, reason: 'Error checking access', limit: 0, used: 0 };
    }
  },

  /**
   * Get feature usage for current user
   * @param {string} featureName - Feature name
   * @returns {Promise<{usageCount, lastUsedAt, resetAt}>}
   */
  async getFeatureUsage(featureName) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { usageCount: 0, lastUsedAt: null, resetAt: null };
      }
      
      const { data, error } = await supabase
        ?.from('feature_usage')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('feature_name', featureName)
        ?.maybeSingle();
      
      if (error) {
        logger?.error('Failed to fetch feature usage', error, { requestId, featureName });
        return { usageCount: 0, lastUsedAt: null, resetAt: null };
      }
      
      if (!data) {
        return { usageCount: 0, lastUsedAt: null, resetAt: null };
      }
      
      return {
        usageCount: data?.usage_count,
        lastUsedAt: data?.last_used_at,
        resetAt: data?.reset_at
      };
    } catch (error) {
      logger?.error('Feature usage fetch error', error, { requestId, featureName });
      return { usageCount: 0, lastUsedAt: null, resetAt: null };
    }
  },

  /**
   * Increment feature usage count
   * @param {string} featureName - Feature name
   * @returns {Promise<{data, error}>}
   */
  async incrementFeatureUsage(featureName) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      // Get current usage
      const { data: existing } = await supabase
        ?.from('feature_usage')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('feature_name', featureName)
        ?.maybeSingle();
      
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      
      if (existing) {
        // Check if we need to reset (new month)
        const resetDate = new Date(existing?.reset_at);
        if (now >= resetDate) {
          // Reset usage for new month
          const { data, error } = await supabase
            ?.from('feature_usage')
            ?.update({
              usage_count: 1,
              last_used_at: now?.toISOString(),
              reset_at: nextMonth?.toISOString()
            })
            ?.eq('user_id', user?.id)
            ?.eq('feature_name', featureName)
            ?.select()
            ?.single();
          
          if (error) {
            logger?.error('Failed to reset feature usage', error, { requestId, featureName });
            return { data: null, error };
          }
          
          return { data, error: null };
        } else {
          // Increment existing usage
          const { data, error } = await supabase
            ?.from('feature_usage')
            ?.update({
              usage_count: existing?.usage_count + 1,
              last_used_at: now?.toISOString()
            })
            ?.eq('user_id', user?.id)
            ?.eq('feature_name', featureName)
            ?.select()
            ?.single();
          
          if (error) {
            logger?.error('Failed to increment feature usage', error, { requestId, featureName });
            return { data: null, error };
          }
          
          return { data, error: null };
        }
      } else {
        // Create new usage record
        const { data, error } = await supabase
          ?.from('feature_usage')
          ?.insert([{
            user_id: user?.id,
            feature_name: featureName,
            usage_count: 1,
            last_used_at: now?.toISOString(),
            reset_at: nextMonth?.toISOString()
          }])
          ?.select()
          ?.single();
        
        if (error) {
          logger?.error('Failed to create feature usage', error, { requestId, featureName });
          return { data: null, error };
        }
        
        logger?.audit('FEATURE_USAGE_TRACKED', user?.id, { requestId, featureName });
        return { data, error: null };
      }
    } catch (error) {
      logger?.error('Feature usage increment error', error, { requestId, featureName });
      return { data: null, error: { message: 'Failed to track feature usage' } };
    }
  },

  /**
   * Get billing history for current user
   * @param {number} limit - Number of records to fetch
   * @returns {Promise<{data, error}>}
   */
  async getBillingHistory(limit = 10) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      const { data, error } = await supabase
        ?.from('billing_history')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);
      
      if (error) {
        logger?.error('Failed to fetch billing history', error, { requestId, userId: user?.id });
        return { data: null, error };
      }
      
      // Convert snake_case to camelCase
      const history = data?.map(record => ({
        id: record?.id,
        userId: record?.user_id,
        subscriptionId: record?.subscription_id,
        amount: record?.amount,
        currency: record?.currency,
        billingInterval: record?.billing_interval,
        paymentStatus: record?.payment_status,
        stripeInvoiceId: record?.stripe_invoice_id,
        paidAt: record?.paid_at,
        createdAt: record?.created_at
      }));
      
      logger?.debug('Billing history fetched', { requestId, count: history?.length });
      return { data: history, error: null };
    } catch (error) {
      logger?.error('Billing history fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch billing history' } };
    }
  },

  /**
   * Get payment failures for current user
   * @param {boolean} unresolvedOnly - Only fetch unresolved failures
   * @returns {Promise<{data, error}>}
   */
  async getPaymentFailures(unresolvedOnly = false) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      let query = supabase
        ?.from('payment_failures')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });
      
      if (unresolvedOnly) {
        query = query?.eq('resolved', false);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger?.error('Failed to fetch payment failures', error, { requestId });
        return { data: null, error };
      }
      
      // Convert snake_case to camelCase
      const failures = data?.map(failure => ({
        id: failure?.id,
        userId: failure?.user_id,
        subscriptionId: failure?.subscription_id,
        stripeInvoiceId: failure?.stripe_invoice_id,
        amount: failure?.amount,
        currency: failure?.currency,
        failureReason: failure?.failure_reason,
        attemptCount: failure?.attempt_count,
        nextRetryAt: failure?.next_retry_at,
        resolved: failure?.resolved,
        resolvedAt: failure?.resolved_at,
        createdAt: failure?.created_at,
        updatedAt: failure?.updated_at
      }));
      
      logger?.debug('Payment failures fetched', { requestId, count: failures?.length });
      return { data: failures, error: null };
    } catch (error) {
      logger?.error('Payment failures fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch payment failures' } };
    }
  },

  /**
   * Get subscription status history for current user
   * @param {number} limit - Number of records to fetch
   * @returns {Promise<{data, error}>}
   */
  async getSubscriptionStatusHistory(limit = 50) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      const { data, error } = await supabase
        ?.from('subscription_status_history')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);
      
      if (error) {
        logger?.error('Failed to fetch status history', error, { requestId });
        return { data: null, error };
      }
      
      // Convert snake_case to camelCase
      const history = data?.map(record => ({
        id: record?.id,
        userId: record?.user_id,
        eventType: record?.event_type,
        status: record?.status,
        metadata: record?.metadata,
        createdAt: record?.created_at
      }));
      
      logger?.debug('Status history fetched', { requestId, count: history?.length });
      return { data: history, error: null };
    } catch (error) {
      logger?.error('Status history fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch status history' } };
    }
  },

  /**
   * Check if user has any unresolved payment failures
   * @returns {Promise<{hasFailures: boolean, count: number}>}
   */
  async hasUnresolvedPaymentFailures() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { hasFailures: false, count: 0 };
      }
      
      const { data, error } = await supabase
        ?.from('payment_failures')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.eq('resolved', false);
      
      if (error) {
        logger?.error('Failed to check payment failures', error, { requestId });
        return { hasFailures: false, count: 0 };
      }
      
      const count = data?.length || 0;
      logger?.debug('Payment failures checked', { requestId, count });
      return { hasFailures: count > 0, count };
    } catch (error) {
      logger?.error('Payment failures check error', error, { requestId });
      return { hasFailures: false, count: 0 };
    }
  },

  /**
   * Check if user is on Basic plan
   * @returns {Promise<boolean>}
   */
  async isBasicPlan() {
    try {
      const { data: subscription } = await this.getCurrentSubscription();
      return subscription?.plan?.planType === 'basic';
    } catch (error) {
      logger?.error('Basic plan check error', error);
      return true; // Default to basic if error
    }
  },

  /**
   * Check if user is on Premium plan
   * @returns {Promise<boolean>}
   */
  async isPremiumPlan() {
    try {
      const { data: subscription } = await this.getCurrentSubscription();
      return subscription?.plan?.planType === 'premium' || subscription?.plan?.planType === 'family';
    } catch (error) {
      logger?.error('Premium plan check error', error);
      return false;
    }
  },

  /**
   * Get plan limits for current user
   * @returns {Promise<object>}
   */
  async getPlanLimits() {
    try {
      // Check if user is owner - grant unlimited access
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user?.email?.toLowerCase() === 'kingsley@mybodytoday1.com') {
        return {
          symptomHistoryDays: -1,
          monthlyExports: -1,
          caregiverConnections: -1,
          managedProfiles: -1,
          dataStorageMb: -1
        };
      }
      
      const { data: subscription } = await this.getCurrentSubscription();
      const limits = subscription?.plan?.limits || {};
      
      // Normalize limits to support both key formats
      return {
        symptomHistoryDays: limits?.symptom_history_days || 7,
        monthlyExports: limits?.monthly_exports || limits?.export_limit || 2,
        caregiverConnections: limits?.caregiver_connections || limits?.caregiver_connections_limit || 0,
        managedProfiles: limits?.managed_profiles || limits?.managed_profiles_limit || 0,
        dataStorageMb: limits?.data_storage_mb || 50,
        // Keep original keys for backward compatibility
        ...limits
      };
    } catch (error) {
      logger?.error('Plan limits fetch error', error);
      return {
        symptomHistoryDays: 7,
        monthlyExports: 2,
        caregiverConnections: 0,
        managedProfiles: 0,
        dataStorageMb: 50
      };
    }
  },

  /**
   * Check if user is currently in trial period
   * @returns {Promise<{inTrial: boolean, trialEnd: string|null}>}
   */
  async isUserInTrial() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: subscription } = await this.getCurrentSubscription();
      if (!subscription) {
        return { inTrial: false, trialEnd: null };
      }
      
      const now = new Date();
      const trialEnd = subscription?.trialEnd ? new Date(subscription?.trialEnd) : null;
      const isBasicPlan = subscription?.plan?.planType === 'basic';
      const inTrial = isBasicPlan && trialEnd && trialEnd > now;
      
      logger?.debug('Trial status checked', { requestId, inTrial, trialEnd });
      return { 
        inTrial: inTrial || false, 
        trialEnd: trialEnd?.toISOString() || null,
        daysRemaining: inTrial ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)) : 0
      };
    } catch (error) {
      logger?.error('Trial status check error', error, { requestId });
      return { inTrial: false, trialEnd: null, daysRemaining: 0 };
    }
  },

  /**
   * Get upgrade recommendation based on current subscription and usage
   * @returns {Promise<{shouldUpgrade: boolean, recommendedPlan: string, reason: string}>}
   */
  async getUpgradeRecommendation() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      // Check if user is owner - no upgrade needed
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user?.email?.toLowerCase() === 'kingsley@mybodytoday1.com') {
        return { shouldUpgrade: false, recommendedPlan: null, reason: 'Owner account - full access' };
      }

      const { data: subscription } = await this.getCurrentSubscription();
      if (!subscription) {
        return { shouldUpgrade: false, recommendedPlan: null, reason: '' };
      }

      // Check trial status
      const trialInfo = await this.isUserInTrial();
      const { inTrial, daysRemaining } = trialInfo;

      // Recommend upgrade if trial is ending soon (within 7 days)
      if (inTrial && daysRemaining <= 7 && daysRemaining > 0) {
        return {
          shouldUpgrade: true,
          recommendedPlan: 'basic_ultra',
          reason: `Your trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Upgrade to Basic Ultra for just £1.99/month to keep your data and access 15 days of history.`
        };
      }

      // Recommend upgrade if trial has ended and user is on basic plan
      if (subscription?.plan?.planType === 'basic' && !inTrial) {
        return {
          shouldUpgrade: true,
          recommendedPlan: 'basic_ultra',
          reason: 'Your trial has ended. Upgrade to Basic Ultra for £1.99/month to continue tracking with 15 days of history.'
        };
      }
      
      return { shouldUpgrade: false, recommendedPlan: null, reason: 'No upgrade needed' };
    } catch (error) {
      logger?.error('Upgrade recommendation error', error, { requestId });
      return { shouldUpgrade: false, recommendedPlan: null, reason: 'Error checking upgrade status' };
    }
  }
};

export default subscriptionService;